import { useId, useRef } from "react"
import type { KeyboardEvent } from "react"
import { useI18n } from "../../../i18n"
import { formatModelFacts, groupBySubstrate, substrateDisplayName } from "../../../shared/model-facts"
import { ToggleSwitch } from "../ToggleSwitch"
import type { PanelProps } from "../types"

// Relocated from SettingsPanel.tsx's CHAT (default model) section, plus the
// reasoning toggles (model-adjacent, not companion-adjacent — they moved out
// of the old CHAT section here rather than to CompanionPanel).
//
// C4: the plain <select> is replaced with substrate-grouped radio rows
// (CLAUDE CODE / CODEX CLI / HERMES) and the four-fact monospace subline —
// same grouping and same formatModelFacts() call as the composer dropdown
// (InputControls.tsx), so the two surfaces cannot disagree.
export function ModelsPanel({
  availableModels,
  currentModel,
  onModelChange,
  showReasoning,
  prefs,
  updatePref,
  sendReasoningConfig,
}: PanelProps) {
  const { t } = useI18n()
  const showReasoningLabelId = useId()
  const readReasoningAloudLabelId = useId()
  const radiogroupRef = useRef<HTMLDivElement>(null)

  // role="radio" roving-tabindex: ArrowUp/ArrowDown move focus across every
  // row regardless of substrate group. Mirrors InputControls.tsx's dropdown —
  // same minimal APG pattern, both surfaces share formatModelFacts() rows.
  function handleRadiogroupKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
    const items = Array.from(
      radiogroupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]') ?? []
    )
    if (items.length === 0) return
    e.preventDefault()
    const activeIndex = items.indexOf(document.activeElement as HTMLElement)
    const delta = e.key === "ArrowDown" ? 1 : -1
    const next = activeIndex === -1 ? 0 : (activeIndex + delta + items.length) % items.length
    items[next].focus()
  }

  return (
    <>
      <div role="radiogroup" aria-label={t("settings.chat.defaultModel")} ref={radiogroupRef} onKeyDown={handleRadiogroupKeyDown}>
        {/* Rolling-alias rows ("Opus · latest" etc) stay in availableModels for
            resolution elsewhere (currentModel, confirm-dialog labels) but are
            never OFFERED here — mirrors InputControls.tsx's composer dropdown.
            Exception: if currentModel itself is a filtered-out alias (a
            session still on "Opus · latest"), keep that ONE row so it renders
            checked with its own honest label — unlike InputControls, this
            radiogroup IS the current-model display (no separate closed-state
            label), so dropping it entirely would show no selection at all. */}
        {groupBySubstrate(availableModels.filter((m) => !m.rollingAlias || m.id === currentModel.id)).map((group) => {
          const groupHeadingId = `settings-model-group-${group.substrate}`
          return (
            <div key={group.substrate} className="settings-model-group" role="group" aria-labelledby={groupHeadingId}>
              <div className="settings-section-label" id={groupHeadingId}>{substrateDisplayName(group.substrate)}</div>
              {group.models.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  role="radio"
                  aria-checked={m.id === currentModel.id}
                  tabIndex={m.id === currentModel.id ? 0 : -1}
                  className="settings-model-row"
                  onClick={() => onModelChange(m.id)}
                >
                  <span className="settings-model-radio" aria-hidden="true" />
                  <span>
                    <span className="settings-model-label">{m.label}</span>
                    <div className="settings-model-facts">{formatModelFacts(m)}</div>
                  </span>
                </button>
              ))}
            </div>
          )
        })}
      </div>

      <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: "var(--space-4)" }}>
        {t("settings.chat.defaultsNote")}
      </div>

      {/* Show reasoning toggle */}
      <div className="settings-row" style={{ marginTop: "var(--space-4)" }}>
        <div>
          <div className="settings-row-label" id={showReasoningLabelId}>{t("settings.models.showReasoning")}</div>
          <div className="settings-row-hint">{t("settings.models.showReasoningHint")}</div>
        </div>
        <ToggleSwitch
          checked={showReasoning ?? prefs.showReasoning}
          labelledBy={showReasoningLabelId}
          onChange={() => {
            const next = !(showReasoning ?? prefs.showReasoning);
            updatePref("showReasoning", next);
            sendReasoningConfig?.(next, prefs.readReasoningAloud);
          }}
        />
      </div>

      {/* Read reasoning aloud — only visible when showReasoning is ON */}
      {(showReasoning ?? prefs.showReasoning) && (
        <div className="settings-row settings-row--sub">
          <div>
            <div className="settings-row-label" id={readReasoningAloudLabelId}>{t("settings.models.readReasoningAloud")}</div>
            <div className="settings-row-hint">{t("settings.models.readReasoningAloudHint")}</div>
          </div>
          <ToggleSwitch
            checked={prefs.readReasoningAloud}
            labelledBy={readReasoningAloudLabelId}
            onChange={() => {
              const next = !prefs.readReasoningAloud;
              updatePref("readReasoningAloud", next);
              sendReasoningConfig?.(showReasoning ?? prefs.showReasoning ?? true, next);
            }}
          />
        </div>
      )}
    </>
  )
}
